import React, {Dispatch, forwardRef, FunctionComponent, SetStateAction, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import {BizFormFileUrlsProviderProps, BizFormForm, BizFormFormResponse, BizFormQuestion, BizFormQuestionOption, BizFormQuestionTypeIds} from "../../index";
import Form, {FinalForm, MuiRFF, ReactFinalForm, Yup} from "../../../../ui/components/form";
import {ApiResponseDs, SubmitFormRequest, SubmitFormRequestQuestionsResponseEntry, UpdateFormResponseRequest} from "../../core/services/api";
import {BizFormApiProvider, useBizFormApi, WithBizFormApiProviderOptions} from "../context/biz-form-api-context";
import {BizFormFileUrlsProvider, useBizFormFileUrls} from "../context/biz-form-file-urls-context";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import useIsMounted from "../hooks/use-is-mounted";
import axios, {CancelToken} from "axios";
import {Box, Button, Card, FormHelperText, IconButton, Skeleton, Tooltip, Typography} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import {Clear, RestartAlt} from "@mui/icons-material";
import DropzoneField from "../../../../ui/components/text-fields/dropzone-field";
// @ts-ignore
import {ReactComponent as CheckboxIcon} from "../../../../assets/images/check-box/checkbox.svg";
// @ts-ignore
import {ReactComponent as ActiveCheckboxIcon} from "../../../../assets/images/check-box/checkbox-active.svg";
import DateUtils from "../../../../core/services/date-utils";
import ToggleButtons from "../../../../ui/components/base/button/toggle_buttons";
import {Field} from "react-final-form";
import {InputFileAcceptMimes, InputFileAcceptTypes} from "../../../../core/constants/enums";
import FileUtils from "../../core/services/file-utils";

export interface BizFormAnswerFormRef {
    submit: (newResponse: boolean) => Promise<void>;
    clear: () => void;
    reset: () => void;
}


export interface BizFormAnswerFormProps extends WithBizFormApiProviderOptions, Omit<BizFormFileUrlsProviderProps, 'children'> {
    formId: string;
    /** When set, external-user APIs are used. External users cannot update existing responses (view-only). */
    externalUserId?: string;
    formResponseId?: string;
    onSubmitted: (responseId: string) => void;
    newResponse: boolean;
    showClearButton?: boolean;
    showResetButton?: boolean;
    showSubmitButton?: boolean;
    setHasErrors?: (hasErrors: boolean) => void;
}

const generateName = (id: any) => `question-${id}`;
const extractIdFromName = (name: any) => Number(name.replace('question-', ''));

function normalizeForm(data: BizFormForm): BizFormForm {
    const questions = [...(data.questions ?? [])];
    questions.sort((a, b) => a.orderIndex - b.orderIndex);
    return {
        ...data,
        questions: questions.map((question) => {
            const options = [...(question.options ?? [])];
            options.sort((a, b) => a.orderIndex - b.orderIndex);
            return {
                ...question,
                options,
            };
        }),
    };
}

const BizFormAnswerFormInner = forwardRef<BizFormAnswerFormRef, BizFormAnswerFormProps>((props, ref) => {
    const api = useBizFormApi();
    const {onSubmitted} = props;

    const isExternalUserMode = Boolean(props.externalUserId?.trim());
    const externalUserId = props.externalUserId?.trim() ?? '';
    /** External users may view a prior submission but cannot edit it. */
    const isExternalReadOnly = isExternalUserMode && Boolean(props.formResponseId) && !props.newResponse;

    const [form, setForm] = useState<BizFormForm>();
    const [loadingForm, setLoadingForm] = useState(true);
    const [loadingFormResponse, setLoadingFormResponse] = useState(false);
    const [formResponse, setFormResponse] = useState<BizFormFormResponse>();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<ApiResponseDs>();
    const [uploadingRecord, setUploadingRecord] = useState<Record<string, boolean>>({});
    const formRef = useRef<FinalForm.FormApi>();
    const isMounted = useIsMounted();

    const uploading = useMemo(() => Object.values(uploadingRecord).some((value) => value), [uploadingRecord]);
    const loading = useMemo(() => loadingForm || loadingFormResponse, [loadingForm, loadingFormResponse]);

    /**
     * Fetches the form by the form token.
     */
    const fetchForm = useCallback(async (cancelToken?: CancelToken) => {
        setLoadingForm(true);
        const response = isExternalUserMode
            ? await api.getFormForExternalUser({formId: props.formId, externalUserId}, cancelToken)
            : await api.getForm({id: props.formId}, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoadingForm(false);
        }
        if (response?.resultFlag && !!response.data) {
            setForm(normalizeForm(response.data));
        } else {
            setError(response);
        }
    }, [api, externalUserId, isExternalUserMode, isMounted, props.formId])

    /**
     * As soon as the component mounts:
     * - fetches the form from the server.
     */
    useEffect(() => {
        if (!props.formId) {
            return
        }
        const cancelTokenSource = axios.CancelToken.source();
        fetchForm(cancelTokenSource.token).then();
        return () => cancelTokenSource.cancel();
    }, [fetchForm, props.formId])

    /**
     * Fetches the form response by the form response token.
     */
    const fetchFormResponse = useCallback(async (cancelToken?: CancelToken) => {
        setUploadingRecord({});
        if (!props.formResponseId || (isExternalUserMode && props.newResponse)) {
            return;
        }
        setLoadingFormResponse(true);
        const response = isExternalUserMode
            ? await api.getResponseForExternalUser({id: props.formResponseId, externalUserId}, cancelToken)
            : await api.getFormResponse({id: props.formResponseId}, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoadingFormResponse(false);
        }
        if (response?.resultFlag && !!response.data) {
            setFormResponse(response.data);
        }
    }, [api, externalUserId, isExternalUserMode, isMounted, props.formResponseId, props.newResponse])

    /**
     * As soon as the component mounts:
     * - fetches the form response from the server.
     */
    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source();
        fetchFormResponse(cancelTokenSource.token).then();
        return () => cancelTokenSource.cancel();
    }, [fetchFormResponse])

    const schema = useMemo(() => {
        const schemaShape = {
            newResponse: Yup.boolean().nullable(),
        };
        for (const question of form?.questions ?? []) {
            const name = generateName(question.id);
            switch (question.questionTypeId) {
                case BizFormQuestionTypeIds.shortAnswer:
                case BizFormQuestionTypeIds.longAnswer:
                case BizFormQuestionTypeIds.file:
                case BizFormQuestionTypeIds.image:
                default:
                    schemaShape[name] = Yup.string().nullable();
                    break;
                case BizFormQuestionTypeIds.select:
                case BizFormQuestionTypeIds.trueFalse:
                    schemaShape[name] = Yup.string().nullable();
                    break;
                case BizFormQuestionTypeIds.multiSelect:
                    schemaShape[name] = Yup.array().of(Yup.number().nullable()).nullable();
                    break;
                case BizFormQuestionTypeIds.date:
                case BizFormQuestionTypeIds.time:
                case BizFormQuestionTypeIds.dateTime:
                    schemaShape[name] = (Yup.mixed() as any).validDate(ValidateMessages.incorrect).nullable()
                    break;
            }
            if (question.isRequired) {
                schemaShape[name] = schemaShape[name].nullable().required(ValidateMessages.required);
            }
        }
        return Yup.object().shape(schemaShape)
    }, [form?.questions])

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => {
        const initialValues = {};
        for (const question of form?.questions ?? []) {
            const name = generateName(question.id);
            const answer = formResponse?.questionResponses.find((answer) => answer.questionId === question.id);
            if (!answer) {
                initialValues[name] = null;
                continue;
            }
            switch (question.questionTypeId) {
                case BizFormQuestionTypeIds.shortAnswer:
                case BizFormQuestionTypeIds.longAnswer:
                case BizFormQuestionTypeIds.file:
                case BizFormQuestionTypeIds.image:
                default:
                    initialValues[name] = answer.content || null;
                    break;
                case BizFormQuestionTypeIds.select:
                case BizFormQuestionTypeIds.trueFalse:
                    initialValues[name] = answer.optionResponses?.map(e => e.optionId)?.[0]?.toString() ?? '';
                    break;
                case BizFormQuestionTypeIds.multiSelect:
                    initialValues[name] = answer.optionResponses?.map(e => e.optionId) ?? [];
                    break;
                case BizFormQuestionTypeIds.date:
                case BizFormQuestionTypeIds.time:
                case BizFormQuestionTypeIds.dateTime:
                    initialValues[name] = answer.content ? DateUtils.serverToUTC(answer.content) : undefined;
                    break;
            }
        }
        return initialValues
    }, [form?.questions, formResponse?.questionResponses])

    /**
     * Sets the defined values with the current form values and then proceeds to the target of the dialog.
     */
    const submitForm = useCallback(async (values: any) => {
        const answers: SubmitFormRequestQuestionsResponseEntry[] = [];
        const valuesEntries = Object.entries(values);
        // check if there are questions that do not have valuesEntries for, then we have to put null for them
        for (const question of form?.questions ?? []) {
            const name = generateName(question.id);
            if (!valuesEntries.find(([entryName]) => entryName === name)) {
                answers.push({
                    questionId: question.id,
                    content: null,
                    options: null,
                });
            }
        }
        for (const [name, value] of valuesEntries) {
            const questionId = extractIdFromName(name);
            const question = form?.questions.find((question) => question.id === questionId);
            if (!question)
                continue;
            let answer: SubmitFormRequestQuestionsResponseEntry | null = null;
            switch (question.questionTypeId) {
                case BizFormQuestionTypeIds.shortAnswer:
                case BizFormQuestionTypeIds.longAnswer:
                case BizFormQuestionTypeIds.file:
                case BizFormQuestionTypeIds.image:
                default:
                    answer = {
                        questionId: question.id,
                        content: value?.toString() ?? null,
                        options: null,
                    };
                    break;
                case BizFormQuestionTypeIds.select:
                case BizFormQuestionTypeIds.trueFalse:
                    answer = {
                        questionId: question.id,
                        content: null,
                        options: value ? [{id: Number(value)}] : null,
                    };
                    break;
                case BizFormQuestionTypeIds.multiSelect:
                    answer = {
                        questionId: question.id,
                        content: null,
                        options: (value as Array<number>)?.length ? (value as Array<number>).map(e => ({id: e})) : null,
                    };
                    break;
                case BizFormQuestionTypeIds.date:
                    answer = {
                        questionId: question.id,
                        content: value ? DateUtils.UTCToServer(value as string).format(DateUtils.withoutOffsetFormat) : null,
                        options: null,
                    };
                    break;
                case BizFormQuestionTypeIds.time:
                    answer = {
                        questionId: question.id,
                        content: value ? DateUtils.UTCToServer(value as string).format(DateUtils.withoutOffsetFormat) : null,
                        options: null,
                    };
                    break;
                case BizFormQuestionTypeIds.dateTime:
                    answer = {
                        questionId: question.id,
                        content: value ? DateUtils.UTCToServer(value as string).format(DateUtils.withoutOffsetFormat) : null,
                        options: null,
                    };
                    break;
            }
            if (answer) {
                answers.push(answer);
            }
        }
        if (isExternalReadOnly) {
            return;
        }

        setSubmitting(true);
        const isNewSubmission = values.newResponse || !props.formResponseId;

        if (isExternalUserMode) {
            if (!isNewSubmission) {
                setSubmitting(false);
                return;
            }
            const response = await api.submitFormForExternalUser({formId: props.formId, externalUserId, questionsResponse: answers});
            if (!isMounted()) {
                return;
            }
            setSubmitting(false);
            if (response?.resultFlag && response.data?.id) {
                onSubmitted(response.data.id);
            }
            return;
        }

        if (isNewSubmission) {
            const forApi: SubmitFormRequest = {formId: props.formId, questionsResponse: answers};
            const response = await api.submitForm(forApi);
            if (!isMounted()) {
                return;
            }
            setSubmitting(false);
            if (response?.resultFlag && response.data?.id) {
                onSubmitted(response.data.id);
            }
        } else {
            const forApi: UpdateFormResponseRequest = {
                id: props.formResponseId!,
                questionsResponse: answers.map(e => ({
                    ...e,
                    options: e.options?.map(e => ({optionId: e.id})) ?? null,
                })),
            };
            const response = await api.updateFormResponse(forApi);
            if (!isMounted()) {
                return;
            }
            setSubmitting(false);
            if (response?.resultFlag) {
                onSubmitted(props.formResponseId!);
            }
        }
    }, [
        api,
        externalUserId,
        form?.questions,
        isExternalReadOnly,
        isExternalUserMode,
        isMounted,
        onSubmitted,
        props.formId,
        props.formResponseId,
    ]);

    const refActions = useMemo<BizFormAnswerFormRef>(() => ({
        submit: async (newResponse: boolean) => {
            if (isExternalReadOnly) {
                return;
            }
            formRef.current?.change('newResponse', newResponse);
            formRef.current?.submit();
        },
        clear: () => {
            formRef.current?.reset({});
            formRef.current?.getRegisteredFields().forEach((name) => {
                formRef.current?.resetFieldState(name);
            })
        },
        reset: () => {
            formRef.current?.reset(initialValues);
            formRef.current?.getRegisteredFields().forEach((name) => {
                formRef.current?.resetFieldState(name);
            })
        }
    }), [initialValues, isExternalReadOnly])

    const showClearButton = props.showClearButton && !isExternalReadOnly;
    const showResetButton = props.showResetButton && !isExternalReadOnly;
    const showSubmitButton = props.showSubmitButton && !isExternalReadOnly;

    /**
     * Provides the form actions to the parent component.
     */
    useImperativeHandle(ref, () => refActions, [refActions])

    return (
        <>
            <Card variant={'elevation'} sx={{boxShadow: 'none', backgroundImage: 'none', padding: 2, width: '100%'}}>
                <Grid container spacing={2}>
                    <Grid xs>
                        {
                            loadingForm &&
                            <Skeleton variant={'rounded'} height={30} sx={{width: '50%'}}/>
                        }
                        {
                            !loadingForm &&
                            <Typography variant={'h6'} fontWeight={600}>
                                {form?.title ?? ""}
                            </Typography>
                        }
                    </Grid>
                    <Grid xs={'auto'} sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1}}>
                        {
                            loadingForm &&
                            <>
                                <Skeleton variant={'circular'} width={30} height={30}/>
                                <Skeleton variant={'circular'} width={30} height={30}/>
                                <Skeleton variant={'rounded'} width={100} height={30}/>
                            </>
                        }
                        {
                            !loadingForm && !error &&
                            <>
                                {
                                    showClearButton &&
                                    <Tooltip title={'Clear Form'}>
                                        <Box component={'span'}>
                                            <IconButton
                                                color={'default'}
                                                disabled={submitting || uploading}
                                                onClick={refActions.clear}
                                                size={'small'}
                                            >
                                                <Clear/>
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                }
                                {
                                    showResetButton &&
                                    <Tooltip title={'Reset Form'}>
                                        <Box component={'span'}>
                                            <IconButton
                                                color={'default'}
                                                disabled={submitting || uploading}
                                                onClick={refActions.reset}
                                                size={'small'}
                                            >
                                                <RestartAlt/>
                                            </IconButton>
                                        </Box>
                                    </Tooltip>
                                }
                                {
                                    showSubmitButton &&
                                    <Button
                                        color={'primary'}
                                        disabled={submitting || uploading}
                                        onClick={() => refActions.submit(props.newResponse)}
                                        size={'small'}
                                    >
                                        {
                                            submitting
                                                ? 'Submitting...'
                                                : 'Submit'
                                        }
                                    </Button>
                                }
                            </>
                        }

                    </Grid>
                    <Grid xs={12}>
                        {
                            loadingForm &&
                            <>
                                <Skeleton variant={'rounded'} height={20} sx={{width: '100%', marginBlockEnd: 0.5}}/>
                                <Skeleton variant={'rounded'} height={20} sx={{width: '100%', marginBlockEnd: 0.5}}/>
                                <Skeleton variant={'rounded'} height={20} sx={{width: '50%', marginBlockEnd: 0.5}}/>
                            </>
                        }
                        {
                            !loadingForm &&
                            <Typography variant={'body2'}>
                                {form?.description ?? ""}
                            </Typography>
                        }
                    </Grid>
                </Grid>
            </Card>
            <Form
                onSubmit={submitForm}
                validate={validate}
                initialValues={initialValues}
                formProps={{
                    className: 'w-100 p-3',
                }}
            >
                {({form: formApi, ...formProps}) => {
                    formRef.current = formApi;
                    return (
                        <BizFormAnswerFormContent
                            loading={loading}
                            form={form}
                            formProps={{form: formApi, ...formProps}}
                            required={required}
                            readOnly={isExternalReadOnly}
                            submitting={submitting}
                            setUploadingRecord={setUploadingRecord}
                            error={error}
                            fetchForm={fetchForm}
                            setHasErrors={props.setHasErrors}
                        />
                    );
                }}
            </Form>
        </>
    );
})


export interface BizFormAnswerFormContentProps {
    loading: boolean;
    form: BizFormForm | null | undefined;
    formProps: ReactFinalForm.FormRenderProps<any, any>;
    required: Record<string, boolean>;
    readOnly?: boolean;
    submitting: boolean;
    setUploadingRecord: Dispatch<SetStateAction<Record<string, boolean>>>;
    error?: ApiResponseDs;
    fetchForm: () => void;
    setHasErrors?: (hasErrors: boolean) => void;
}


const BizFormAnswerFormContent: FunctionComponent<BizFormAnswerFormContentProps> = (props) => {
    const errorsRef = useRef<HTMLDivElement>(null);
    const alreadyScrolledRef = useRef(false);
    const {setHasErrors} = props;

    /**
     * Sets the error state based on the form validation and submission errors.
     */
    useEffect(() => {
        const hasErrors =
            (props.formProps.hasValidationErrors || props.formProps.hasSubmitErrors) && (props.formProps.submitFailed);
        setHasErrors?.(hasErrors);
    }, [props.formProps.hasValidationErrors, props.formProps.hasSubmitErrors, props.formProps.submitFailed, setHasErrors])

    /**
     * Scrolls to the errors section when the form has submission errors.
     */
    useEffect(() => {
        if (alreadyScrolledRef.current)
            return;
        alreadyScrolledRef.current = true;
        errorsRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
    }, [props.formProps.hasSubmitErrors, props.formProps.submitFailed])

    return (
        <>
            {
                !props.loading && !props.form &&
                <Card variant={'outlined'} sx={{padding: 2, marginBlock: 'auto'}}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        paddingBlock: 4,
                        width: '100%'
                    }}>
                        <Typography variant={'body2'} textAlign={'center'}>
                            We are so sorry but the form could not be fetched. <br/>
                            {props.error?.message ?? ""} <br/>
                            {
                                props.error?.referenceNumber
                                    ? <>` Reference no: ${props.error.referenceNumber}` <br/> </>
                                    : ""
                            }
                            {props.error?.code ? ` Error Code: ${props.error.code}` : ""}
                        </Typography>
                        <Button size={'medium'} variant={'outlined'} onClick={() => props.fetchForm()}>
                            Retry fetching the form.
                        </Button>
                    </Box>
                </Card>
            }
            {
                (props.loading || !!props.form) &&
                <Grid container spacing={2} margin={0} rowGap={3}>
                    {
                        props.loading &&
                        Array.from({length: 6}).map((_, e) => (
                            <Grid xs={12} key={`loading-${e}`} sx={{display: 'flex', flexDirection: 'column', gap: 2,}}>
                                <Skeleton variant={'rounded'} height={30} sx={{width: '50%'}}/>
                                <Skeleton variant={'rounded'} height={44} sx={{width: '100%'}}/>
                            </Grid>
                        ))
                    }
                    {
                        !props.loading &&
                        <>
                            {
                                (props.formProps.hasValidationErrors || props.formProps.hasSubmitErrors) && (props.formProps.submitFailed) &&
                                <Grid xs={12} ref={errorsRef}>
                                    <BizFormAnswerFormErrors
                                        errors={props.formProps.errors}
                                        questions={props.form?.questions ?? []}
                                    />
                                </Grid>
                            }
                            {
                                props.form?.questions.map((question) => (
                                    <Grid key={question.id} xs={12}>
                                        <BizFormAnswerFormQuestion
                                            question={question}
                                            disabled={props.submitting || (props.readOnly ?? false)}
                                            required={props.required[generateName(question.id)]}
                                            onChange={(value) => props.formProps.form.change(generateName(question.id), value)}
                                            value={props.formProps.values[generateName(question.id)]}
                                            setUploading={(value) => props.setUploadingRecord((prev) => ({
                                                ...prev,
                                                [question.id]: typeof value === 'function' ? value(prev[question.id]) : value
                                            }))}
                                        />
                                    </Grid>
                                ))
                            }
                        </>
                    }
                </Grid>
            }
        </>
    );
}


export interface BizFormAnswerFormErrorsProps {
    errors: FinalForm.ValidationErrors;
    questions: BizFormQuestion[];
}

const BizFormAnswerFormErrors: FunctionComponent<BizFormAnswerFormErrorsProps> = (props) => {
    const errorsList = useMemo(() => {
        return Object.entries(props.errors ?? {}).map(([name, error]) => ({
            name: name,
            questionTitle: props.questions.find(e => e.id === extractIdFromName(name))?.content ?? "",
            errors: Array.isArray(error) ? error.join(' & ') : error?.toString(),
        }))
    }, [props.errors, props.questions])

    return (
        <>
            <Typography variant={'body2'} color={'error'} sx={{fontWeight: 600}}>
                Please correct the following errors:
            </Typography>
            {
                errorsList.map((error) => (
                    <FormHelperText key={error.name} error>
                        <Typography
                            variant={'caption'}
                            fontWeight={600}
                            component={'span'}
                            sx={{marginInlineEnd: 0.5}}
                        >
                            {error.questionTitle ?? ""}:
                        </Typography>
                        {error.errors ?? ""}
                    </FormHelperText>
                ))
            }
        </>
    );
}

export interface BizFormAnswerFormQuestionProps {
    question: BizFormQuestion;
    required: boolean;
    disabled: boolean;
    value: any;
    onChange: (value: any) => void;
    setUploading: Dispatch<SetStateAction<boolean>>;
}

const BizFormAnswerFormQuestion: FunctionComponent<BizFormAnswerFormQuestionProps> = (props) => {


    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1,}}>
                <Typography variant={'body1'} fontWeight={600}>
                    {props.question.content ?? ""}
                    {
                        props.required &&
                        <Typography
                            component={'span'}
                            sx={{marginInlineStart: 0.5}}
                        >
                            *
                        </Typography>
                    }
                </Typography>
                <Typography variant={'body2'} color={'text.secondary'}>
                    {props.question.description ?? ""}
                </Typography>
                <BizFormAnswerFormQuestionInput
                    name={generateName(props.question.id)}
                    typeId={props.question.questionTypeId}
                    options={props.question.options}
                    required={props.required}
                    disabled={props.disabled}
                    onChange={props.onChange}
                    setUploading={props.setUploading}
                    value={props.value}
                />
            </Box>
        </>
    );
}

export interface BizFormAnswerFormQuestionInputProps {
    name: string;
    value: any;
    required: boolean;
    disabled: boolean;
    typeId: number;
    setUploading: Dispatch<SetStateAction<boolean>>;
    onChange: (value: any) => void;
    options?: BizFormQuestionOption[];
}

const BizFormAnswerFormQuestionInput: FunctionComponent<BizFormAnswerFormQuestionInputProps> = (props) => {
    const api = useBizFormApi();
    const bizFormFileUrls = useBizFormFileUrls();
    const {setUploading, onChange} = props;

    /**
     * Removes the file with the given ID and returns whether the operation was successful or not.
     */
    const removeFile = useCallback(async (id) => {
        const response = await api.deleteFile(id as string);
        if (response?.resultFlag) {
            return true;
        }
        return response?.errorCode ?? false;
    }, [api])

    switch (props.typeId) {
        case BizFormQuestionTypeIds.shortAnswer:
        case BizFormQuestionTypeIds.longAnswer:
            return (
                <MuiRFF.TextField
                    required={props.required}
                    name={props.name}
                    multiline={props.typeId === BizFormQuestionTypeIds.longAnswer}
                    minRows={props.typeId === BizFormQuestionTypeIds.longAnswer ? 3 : 1}
                    fullWidth
                    disabled={props.disabled}
                />
            );
        case BizFormQuestionTypeIds.file:
        case BizFormQuestionTypeIds.image:
            return (
                <DropzoneField
                    required={props.required}
                    apiClient={api}
                    name={props.name}
                    {...(props.typeId === BizFormQuestionTypeIds.image && {
                        accept: [
                            InputFileAcceptMimes.images,
                            InputFileAcceptTypes.heic,
                            InputFileAcceptTypes.heif,
                        ],
                    })}
                    onChange={(value) => onChange(value as string)}
                    onUploadStart={() => setUploading(true)}
                    onUploadEnd={() => setUploading(false)}
                    fullWidth
                    disabled={props.disabled}
                    getFullFileUrl={(guid) => bizFormFileUrls.getFilePreviewUrl(guid) ?? ''}
                    remove={removeFile}
                    initialFileUpload={(args) =>
                        api.uploadFile({
                            file: args.chunk,
                            fileName: args.fileName,
                            fileSize: args.fileSize,
                        }, args.onUploadProgress)
                    }
                    uploadFile={(...args) => FileUtils.uploadFile(...args)}
                    getFileGuid={(data) => data.id}
                    getFileExtension={(data) => data.extension}
                />
            );
        case BizFormQuestionTypeIds.trueFalse:
            return (
                <Field
                    name={props.name}
                    render={({input, meta}) => {
                        const {error, submitError} = meta;
                        const isError = MuiRFF.showErrorOnChange({meta});
                        return (
                            <>
                                <ToggleButtons
                                    className={'toggle-buttons'}
                                    // color={isError ? 'error' : 'primary'}
                                    value={input.value}
                                    onChange={(e, item) => input.onChange({...e, target: {...(e.target ?? {}), value: item}})}
                                    disabled={props.disabled}
                                    options={props.options?.map((option) => ({
                                        value: option.id.toString(),
                                        name: option.content,
                                        disabled: props.disabled
                                    })) ?? []}
                                />
                                {
                                    (isError) &&
                                    <FormHelperText error={isError}>
                                        {error || submitError}
                                    </FormHelperText>
                                }
                            </>
                        )
                    }}
                />
            );
        case BizFormQuestionTypeIds.select:
            return (
                <MuiRFF.Radios
                    data={props.options?.map((option) => ({
                        value: option.id.toString(),
                        label: option.content,
                        disabled: props.disabled
                    })) ?? []}
                    required={props.required}
                    name={props.name}
                    disabled={props.disabled}
                    formControlLabelProps={{
                        sx: {margin: 0,}
                    }}
                />
            );
        case BizFormQuestionTypeIds.multiSelect:
            return (
                <MuiRFF.Checkboxes
                    icon={<CheckboxIcon/>}
                    checkedIcon={<ActiveCheckboxIcon/>}
                    data={props.options?.map((option) => ({value: option.id, label: option.content, disabled: props.disabled})) ?? []}
                    required={props.required}
                    name={props.name}
                    disabled={props.disabled}
                    formControlLabelProps={{
                        sx: {margin: 0,}
                    }}
                />
            );
        case BizFormQuestionTypeIds.date:
            return (
                <MuiRFF.DatePicker
                    required={props.required}
                    name={props.name}
                    disabled={props.disabled}
                />
            );
        case BizFormQuestionTypeIds.time:
            return (
                <MuiRFF.TimePicker
                    required={props.required}
                    name={props.name}
                    disabled={props.disabled}
                />
            );
        case BizFormQuestionTypeIds.dateTime:
            return (
                <MuiRFF.DateTimePicker
                    required={props.required}
                    name={props.name}
                    disabled={props.disabled}
                />
            );
        default:
            return (
                <></>
            )
    }
}

const BizFormAnswerForm = forwardRef<BizFormAnswerFormRef, BizFormAnswerFormProps>((props, ref) => {
    const {apiClient, apiBaseUrl, apiType, fileInfo, ...formProps} = props;
    const normalizedBaseUrl = apiBaseUrl != null && apiBaseUrl.trim() !== "" ? apiBaseUrl : undefined;

    let content = <BizFormAnswerFormInner {...formProps} ref={ref}/>;

    if (fileInfo) {
        content = (
            <BizFormFileUrlsProvider fileInfo={fileInfo}>
                {content}
            </BizFormFileUrlsProvider>
        )
    }
    if (apiClient != null || normalizedBaseUrl != null) {
        return (
            <BizFormApiProvider baseUrl={normalizedBaseUrl} client={apiClient} type={apiType}>
                {content}
            </BizFormApiProvider>
        );
    }
    return content;
});

export default BizFormAnswerForm;
